import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { T, cardSx, labelSx, MetricCard, SectionLabel, EmptyNote } from "./PlatformAnalyticsShared";

const SIGNAL_LABELS = {
  no_kanban: "No Kanban moves 4+ wks",
  no_reveals: "No CV reveals 4+ wks",
  no_logins: "No logins 4+ wks",
  credits_expiring: "Credits expiring unused",
};

const CLASSIFICATION_STYLE = {
  active: { label: "Active", bg: "rgba(46,125,50,0.10)", color: T.green },
  dormant: { label: "Dormant", bg: "rgba(211,47,47,0.10)", color: "#D32F2F" },
  at_risk: { label: "At risk", bg: "rgba(245,124,0,0.12)", color: "#F57C00" },
  old_client: { label: "Old client", bg: "hsl(140 6% 93%)", color: T.dim },
  no_credits: { label: "No credits", bg: "hsl(140 6% 93%)", color: T.dim },
};

const headCellSx = {
  borderBottom: `1px solid ${T.border}`,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: T.dim,
  py: "10px",
  px: "16px",
  position: "sticky",
  top: 0,
  backgroundColor: T.bg2,
  zIndex: 1,
};

const bodyCellSx = {
  py: "12px",
  px: "16px",
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: T.fg,
  borderBottom: `1px solid ${T.border}`,
};

const formatDate = (value) => {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Never";
  return new Intl.DateTimeFormat("fr-BE", { year: "2-digit", month: "2-digit", day: "2-digit" }).format(d);
};

const RiskTable = ({ rows, emptyLabel, onRowClick }) => {
  if (rows.length === 0) return <EmptyNote>{emptyLabel}</EmptyNote>;

  return (
    <Box sx={{ maxHeight: 560, overflowY: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {["Company", "Status", "Signals", "Recruiters", "Last activity"].map((h) => (
              <TableCell key={h} sx={headCellSx}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const cls = CLASSIFICATION_STYLE[row.classification] || CLASSIFICATION_STYLE.at_risk;
            return (
              <TableRow
                key={row.company_id}
                hover
                onClick={() => onRowClick(row.company_id)}
                sx={{ cursor: "pointer", "&:last-child td": { borderBottom: "none" } }}
              >
                <TableCell sx={{ ...bodyCellSx, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{row.name || `#${row.company_id}`}</TableCell>
                <TableCell sx={bodyCellSx}>
                  <Chip
                    label={cls.label}
                    size="small"
                    sx={{ backgroundColor: cls.bg, color: cls.color, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 10, fontWeight: 600, borderRadius: "6px", height: 20 }}
                  />
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(row.signals || []).map((sig) => (
                      <Chip
                        key={sig}
                        label={SIGNAL_LABELS[sig] || sig}
                        size="small"
                        sx={{ backgroundColor: "hsl(140 6% 93%)", color: T.dim, fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 10, borderRadius: "6px", height: 20 }}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx}>{row.recruiters_active}/{row.recruiters_total} active</TableCell>
                <TableCell sx={bodyCellSx}>{formatDate(row.last_activity)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

const CompanyHealthTab = ({ data }) => {
  const navigate = useNavigate();
  const {
    active = [],
    at_risk: atRisk = [],
    dormant = [],
    old_clients: oldClients = [],
    old_clients_total: oldClientsTotal = 0,
    no_credits: noCredits = [],
    no_credits_total: noCreditsTotal = 0,
    email_openers: emailOpeners = [],
    time_to_hire: tth,
  } = data;

  const tthDelta = tth?.current_days != null && tth?.previous_days != null
    ? Math.round((tth.current_days - tth.previous_days) * 10) / 10
    : null;

  return (
    <Stack spacing={3}>
      <Box>
        <SectionLabel note="Average days from a candidate entering the kanban to reaching the 'hired' column, across all companies. Compared against the previous equivalent period (e.g. this month vs last month, set by the period selector above).">
          Time-to-hire benchmark
        </SectionLabel>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: "16px", mt: 1 }}>
          <MetricCard label="Avg time-to-hire (current)" current={tth?.current_days} suffix="d" barColor={T.blue} scope="days" />
          <MetricCard label="Avg time-to-hire (previous)" current={tth?.previous_days} suffix="d" barColor={T.dim} scope="days" />
          <MetricCard label="Hires (current)" current={tth?.hires_current} previous={tth?.hires_previous} comparisonLabel="vs prev." barColor={T.green} />
          <Box sx={{ ...cardSx, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>Speed trend</Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: tthDelta == null ? T.dim : tthDelta <= 0 ? T.green : T.red }}>
              {tthDelta == null ? "n/a" : tthDelta === 0 ? "No change" : tthDelta < 0 ? `${Math.abs(tthDelta)}d faster` : `${tthDelta}d slower`}
            </Typography>
            <Typography sx={{ fontSize: 10.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: T.dim, mt: 0.5, lineHeight: 1.4 }}>
              Change in average time-to-hire vs the previous period. Faster is better.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box>
        <SectionLabel>Companies needing attention</SectionLabel>
        <Box sx={{ ...cardSx, mt: 1, mb: 1, backgroundColor: "hsl(140 6% 97%)" }}>
          <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: T.fg, mb: 0.75 }}>
            How this is calculated
          </Typography>
          <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: T.dim, lineHeight: 1.55 }}>
            Only client companies, meaning those holding at least one unexpired credit (available or activated), appear in
            the Active, Dormant, At risk and Email openers boards. Each of them is scanned for four risk signals:
          </Typography>
          <Box component="ul" sx={{ m: "8px 0 0", pl: "18px", display: "flex", flexDirection: "column", gap: "4px" }}>
            {[
              ["No Kanban moves 4+ wks", "the company's most recent pipeline card move was more than 4 weeks ago (or never)."],
              ["No CV reveals 4+ wks", "no recruiter from the company has revealed a candidate's CV in the last 4 weeks."],
              ["No logins 4+ wks", "no recruiter from the company has signed in within the last 4 weeks (or ever)."],
              ["Credits expiring unused", "it holds available licence credits that expire within 14 days and haven't been activated."],
            ].map(([term, desc]) => (
              <Box component="li" key={term} sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: T.dim, lineHeight: 1.5 }}>
                <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: T.fg }}>{term}</Box>
                {": "}{desc}
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: T.dim, lineHeight: 1.55, mt: 1 }}>
            <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "#D32F2F" }}>Dormant</Box>
            {" "}means all three engagement signals are stale (no moves, no reveals and no logins). These sort to the top.{" "}
            <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "#F57C00" }}>At risk</Box>
            {" "}trips one or more signals but still shows some recent activity. Rows are ordered by a score that counts the
            signals and weights the three engagement signals (Kanban, reveals, logins) double, then by how recently the
            company was last active. Click any row to open that company&apos;s Activity tab.
          </Typography>
          <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: T.dim, lineHeight: 1.55, mt: 1 }}>
            Companies whose credits have all expired are listed under{" "}
            <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: T.fg }}>Old clients</Box>, and
            companies that never had a credit under{" "}
            <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: T.fg }}>No credits</Box> below.
          </Typography>
        </Box>
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: T.green, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.75 }}>
            Active{active.length > 0 ? ` (${active.length})` : ""}
          </Typography>
          <Box sx={{ ...cardSx, p: 0, overflow: "hidden" }}>
            <RiskTable
              rows={active}
              emptyLabel="No active client companies"
              onRowClick={(id) => navigate(`/internal/companies/${id}`)}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "#D32F2F", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.75 }}>
            Dormant{dormant.length > 0 ? ` (${dormant.length})` : ""}
          </Typography>
          <Box sx={{ ...cardSx, p: 0, overflow: "hidden" }}>
            <RiskTable
              rows={dormant}
              emptyLabel="No dormant accounts 🎉"
              onRowClick={(id) => navigate(`/internal/companies/${id}`)}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "#F57C00", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.75 }}>
            At risk{atRisk.length > 0 ? ` (${atRisk.length})` : ""}
          </Typography>
          <Box sx={{ ...cardSx, p: 0, overflow: "hidden" }}>
            <RiskTable
              rows={atRisk}
              emptyLabel="No at-risk accounts 🎉"
              onRowClick={(id) => navigate(`/internal/companies/${id}`)}
            />
          </Box>
        </Box>
      </Box>

      <Box>
        <SectionLabel note="Only picked from dormant companies: a company that is still at risk or active is never listed here, however its recruiters behave. Within those dormant companies, these are the recruiters who engage with us only through email: they have clicked at least one link in a mail (for example opening a candidate's CV from a new-application email), yet never logged in and never performed any in-app action (no Kanban move, CV reveal or CV download). Their clicked mails must span at least a year, so these are long-lived 'still interested but never activates' accounts, not freshly invited ones. Grouped by company.">
          Email openers{emailOpeners.length > 0 ? ` (${emailOpeners.length})` : ""}
        </SectionLabel>
        <Box sx={{ ...cardSx, p: 0, overflow: "hidden", mt: 1 }}>
          {emailOpeners.length === 0 ? (
            <EmptyNote>No email-only openers</EmptyNote>
          ) : (
            <Box sx={{ maxHeight: 560, overflowY: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Company", "Email openers", "Opens", "Clicks", "Last engaged"].map((h) => (
                      <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emailOpeners.map((row) => (
                    <TableRow
                      key={row.company_id}
                      hover
                      onClick={() => navigate(`/internal/companies/${row.company_id}`)}
                      sx={{ cursor: "pointer", "&:last-child td": { borderBottom: "none" } }}
                    >
                      <TableCell sx={{ ...bodyCellSx, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{row.name || `#${row.company_id}`}</TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Chip
                          label={`${row.recruiters} recruiter${row.recruiters === 1 ? "" : "s"}`}
                          size="small"
                          sx={{ backgroundColor: "rgba(21,101,192,0.10)", color: T.blue, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 10, fontWeight: 600, borderRadius: "6px", height: 20 }}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>{row.opens}</TableCell>
                      <TableCell sx={bodyCellSx}>{row.clicks}</TableCell>
                      <TableCell sx={bodyCellSx}>{formatDate(row.last_engaged_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>

      <Box>
        <SectionLabel note="Companies that had credits with us, all of which have now expired. Sorted by the most recent expiry.">
          Old clients{oldClientsTotal > 0 ? ` (${oldClientsTotal})` : ""}
        </SectionLabel>
        <Box sx={{ ...cardSx, p: 0, overflow: "hidden", mt: 1 }}>
          {oldClients.length === 0 ? (
            <EmptyNote>No old clients</EmptyNote>
          ) : (
            <Box sx={{ maxHeight: 560, overflowY: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Company", "Credits expired on", "Last login", "Last activity"].map((h) => (
                      <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {oldClients.map((row) => (
                    <TableRow
                      key={row.company_id}
                      hover
                      onClick={() => navigate(`/internal/companies/${row.company_id}`)}
                      sx={{ cursor: "pointer", "&:last-child td": { borderBottom: "none" } }}
                    >
                      <TableCell sx={{ ...bodyCellSx, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{row.name || `#${row.company_id}`}</TableCell>
                      <TableCell sx={bodyCellSx}>{formatDate(row.credits_expired_at)}</TableCell>
                      <TableCell sx={bodyCellSx}>{formatDate(row.last_login)}</TableCell>
                      <TableCell sx={bodyCellSx}>{formatDate(row.last_activity)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>

      <Box>
        <SectionLabel note="Companies that never had an available, activated or expired credit with us.">
          No credits{noCreditsTotal > 0 ? ` (${noCreditsTotal})` : ""}
        </SectionLabel>
        <Box sx={{ ...cardSx, p: 0, overflow: "hidden", mt: 1 }}>
          {noCredits.length === 0 ? (
            <EmptyNote>No companies without credits</EmptyNote>
          ) : (
            <Box sx={{ maxHeight: 560, overflowY: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Company", "Recruiters", "Last login", "Last activity"].map((h) => (
                      <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {noCredits.map((row) => (
                    <TableRow
                      key={row.company_id}
                      hover
                      onClick={() => navigate(`/internal/companies/${row.company_id}`)}
                      sx={{ cursor: "pointer", "&:last-child td": { borderBottom: "none" } }}
                    >
                      <TableCell sx={{ ...bodyCellSx, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{row.name || `#${row.company_id}`}</TableCell>
                      <TableCell sx={bodyCellSx}>{row.recruiters_active}/{row.recruiters_total} active</TableCell>
                      <TableCell sx={bodyCellSx}>{formatDate(row.last_login)}</TableCell>
                      <TableCell sx={bodyCellSx}>{formatDate(row.last_activity)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>
    </Stack>
  );
};

export default CompanyHealthTab;
